import { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Images, X, CircleNotch } from "@phosphor-icons/react";
import { compressImage } from "../../lib/imageCompress";

export const MAX_PHOTOS = 20;

export function PhotoPicker({ photos, setPhotos }) {
  const camRef = useRef(null);
  const galRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const add = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const room = MAX_PHOTOS - photos.length;
    if (room <= 0) { toast.error(`Maximum ${MAX_PHOTOS} photos`); return; }
    if (files.length > room) toast.warning(`Only ${room} more photos can be added`);
    setBusy(true);
    const out = [];
    for (const f of files.slice(0, room)) {
      try {
        const c = await compressImage(f);
        out.push({ id: Math.random().toString(36).slice(2), file: c, url: URL.createObjectURL(c) });
      } catch (e) { toast.error(e.message); }
    }
    setPhotos((p) => [...p, ...out]);
    setBusy(false);
  };

  const remove = (id) => setPhotos((p) => p.filter((x) => x.id !== id));

  return (
    <div className="space-y-3" data-testid="photo-picker">
      <div className="flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Dispatch Photos</div>
        <span className="text-[11px] font-mono font-bold bg-slate-900 text-white rounded-full px-2.5 py-0.5" data-testid="photo-count">{photos.length} / {MAX_PHOTOS}</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button type="button" data-testid="photo-camera-btn" disabled={busy} onClick={() => camRef.current?.click()}
          className="h-12 rounded-xl border-2 border-dashed border-slate-300 hover:border-brand-400 hover:bg-brand-50 text-sm font-semibold flex items-center justify-center gap-2 transition-colors">
          <Camera size={18} className="text-brand-600" /> Camera
        </button>
        <button type="button" data-testid="photo-gallery-btn" disabled={busy} onClick={() => galRef.current?.click()}
          className="h-12 rounded-xl border-2 border-dashed border-slate-300 hover:border-brand-400 hover:bg-brand-50 text-sm font-semibold flex items-center justify-center gap-2 transition-colors">
          <Images size={18} className="text-sky-600" /> Gallery
        </button>
      </div>
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="photo-camera-input"
        onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
      <input ref={galRef} type="file" accept="image/*" multiple className="hidden" data-testid="photo-gallery-input"
        onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
      {busy && <div className="text-xs text-slate-500 flex items-center gap-1.5"><CircleNotch size={14} className="animate-spin" /> Optimising photos…</div>}
      {photos.length > 0 && (
        <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
          {photos.map((p, i) => (
            <div key={p.id} className="relative aspect-square rounded-lg overflow-hidden ring-1 ring-slate-200 group" data-testid={`photo-thumb-${i}`}>
              <img src={p.url} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => remove(p.id)} data-testid={`photo-remove-${i}`}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-rose-600"><X size={12} weight="bold" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
